import { Badge, getColorStyles, Text } from '@billy/design-system'
import React, { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useLocale } from '../../../../../../hooks/useLocale'
import { formatDate } from '../../../../../../utils/date-fns'
import { PayrollStatus } from '../../../../types/payrollStatus'
import { statusToBadgeColor } from '../../utils/statusToBadgeVariant'
import { getStatusToLabel } from '../../utils/statusToLabel'

interface PayrollStatusFormatterProps {
  status: PayrollStatus
  approvalDeadline?: string
}

export const PayrollStatusFormatter = ({ approvalDeadline, status }: PayrollStatusFormatterProps): ReactElement => {
  const { t } = useTranslation()
  const { code } = useLocale()

  const getAdditionalInfo = useCallback(() => {
    if (status === PayrollStatus.AwaitingApproval && approvalDeadline) {
      const deadlineDate = formatDate(approvalDeadline, 'd. MMM HH:mm', code)
      return `(${t('salary.status.no_later_than', { deadlineDate })})`
    }

    return ''
  }, [approvalDeadline, code, status, t])

  const statusBadgeLabelMap = useMemo(() => getStatusToLabel(t), [t])
  const badgeColor = statusToBadgeColor[status]
  const badgeLabel = statusBadgeLabelMap[status]
  const additionalInfo = getAdditionalInfo()
  const additionalInfoColor = getColorStyles(badgeColor, 'subtle', false).textColor

  return (
    <>
      <Badge color={badgeColor} mode="subtle">
        {badgeLabel}
      </Badge>

      {additionalInfo && (
        <Text variant="bodySM" color={additionalInfoColor}>
          {additionalInfo}
        </Text>
      )}
    </>
  )
}
